<template>
  <div class="reasoning-steps" :class="{ 'is-root': isRoot }">
    <!-- Root-level header: toggle + expand/collapse all -->
    <div v-if="isRoot && !isLoading" class="reasoning-header">
      <span class="reasoning-toggle" @click="isCollapsed = !isCollapsed">
        <span class="toggle-arrow">{{ isCollapsed ? '▸' : '▾' }}</span>
        {{ $t('chat_box.view_reasoning', { count: steps.length }) }}
      </span>
      <span v-if="!isCollapsed" class="header-actions">
        <span class="action-link" @click="expandAll">{{
          $t('chat_box.expand_all')
        }}</span>
        <span class="action-link" @click="collapseAll">{{
          $t('chat_box.collapse_all')
        }}</span>
      </span>
    </div>

    <div v-if="!isCollapsed || isLoading" class="steps-list">
      <div
        v-for="(step, idx) in steps"
        :key="idx"
        class="step-node"
        :class="{ current: isLoading && idx === steps.length - 1 }"
      >
        <div
          class="step-row"
          :class="{ expandable: hasChildren(step) }"
          @click="hasChildren(step) && toggleNode(idx)"
        >
          <span
            v-if="hasChildren(step)"
            class="node-arrow"
            @click.stop="toggleNode(idx)"
            >{{ openNodes[idx] ? '▾' : '▸' }}</span
          >
          <span v-else class="node-arrow leaf">&nbsp;</span>
          <span class="step-icon" :class="step.step_type || 'default'">{{
            iconFor(step.step_type)
          }}</span>
          <span class="step-title">{{ step.title }}</span>
          <span
            v-if="step.title && step.summary !== step.title"
            class="step-summary"
            >&mdash; {{ step.summary }}</span
          >
          <span v-if="step.elapsed" class="step-time">{{ step.elapsed }}</span>
        </div>

        <!-- Expanded: detail + children -->
        <div v-if="openNodes[idx]" class="step-children">
          <div
            v-if="step.detail && Object.keys(step.detail).length"
            class="step-detail"
          >
            <div
              v-for="(value, key) in step.detail"
              :key="key"
              class="detail-row"
            >
              <span class="detail-key">{{ formatKey(key) }}:</span>
              <pre v-if="isLongValue(value)" class="detail-value-code">{{
                typeof value === 'string'
                  ? value
                  : JSON.stringify(value, null, 2)
              }}</pre>
              <span v-else class="detail-value">{{ formatValue(value) }}</span>
            </div>
          </div>
          <!-- Recursive children -->
          <ReasoningSteps
            v-if="step.children && step.children.length"
            :steps="step.children"
            :isLoading="false"
            :isRoot="false"
            :ref="'child-' + idx"
          />
        </div>
      </div>

      <!-- Thinking indicator during loading -->
      <div v-if="isLoading" class="step-node current pulse">
        <div class="step-row">
          <span class="node-arrow leaf">&nbsp;</span>
          <span class="step-icon default">◉</span>
          <span class="step-title thinking-dots">{{
            $t('chat_box.thinking')
          }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const STEP_ICONS = {
  routing: '🔀',
  retrieval: '🔍',
  augmentation: '💭',
  rerank: '📊',
  response_generation: '⚡',
};

export default {
  name: 'ReasoningSteps',
  props: {
    steps: { type: Array, required: true },
    isLoading: { type: Boolean, default: false },
    isRoot: { type: Boolean, default: true },
  },
  data() {
    return {
      isCollapsed: !this.isLoading && this.isRoot,
      openNodes: {},
    };
  },
  watch: {
    isLoading(val) {
      if (this.isRoot) this.isCollapsed = !val;
    },
  },
  methods: {
    iconFor(type) {
      return STEP_ICONS[type] || '●';
    },
    hasChildren(step) {
      return (
        (step.detail && Object.keys(step.detail).length > 0) ||
        (step.children && step.children.length > 0)
      );
    },
    toggleNode(idx) {
      this.$set(this.openNodes, idx, !this.openNodes[idx]);
    },
    expandAll() {
      this.steps.forEach((step, idx) => {
        if (this.hasChildren(step)) {
          this.$set(this.openNodes, idx, true);
        }
      });
      this.$nextTick(() => {
        Object.keys(this.$refs).forEach((key) => {
          if (key.startsWith('child-') && this.$refs[key]) {
            const child = Array.isArray(this.$refs[key])
              ? this.$refs[key][0]
              : this.$refs[key];
            if (child && child.expandAll) child.expandAll();
          }
        });
      });
    },
    collapseAll() {
      this.openNodes = {};
      this.$nextTick(() => {
        Object.keys(this.$refs).forEach((key) => {
          if (key.startsWith('child-') && this.$refs[key]) {
            const child = Array.isArray(this.$refs[key])
              ? this.$refs[key][0]
              : this.$refs[key];
            if (child && child.collapseAll) child.collapseAll();
          }
        });
      });
    },
    formatKey(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatValue(value) {
      if (value === null || value === undefined) return '\u2014';
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    },
    isLongValue(value) {
      if (typeof value === 'string' && value.length > 80) return true;
      if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value).length > 80;
      }
      return false;
    },
  },
};
</script>

<style scoped>
.reasoning-steps {
  font-size: 12px;
  color: #4a5568;
  width: 100%;
}

.reasoning-steps.is-root {
  padding: 4px 10px;
}

.reasoning-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;
}

.reasoning-toggle {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #5a67d8;
  font-size: 11px;
  user-select: none;
  padding: 2px 4px;
  border-radius: 3px;
}

.reasoning-toggle:hover {
  color: #4c51bf;
  background: #edf2f7;
}

.toggle-arrow {
  font-size: 12px;
}

.header-actions {
  display: flex;
  gap: 8px;
  font-size: 10px;
}

.action-link {
  cursor: pointer;
  color: #718096;
  user-select: none;
}

.action-link:hover {
  color: #5a67d8;
  text-decoration: underline;
}

.steps-list {
  margin-top: 1px;
}

.step-node {
  border-left: 2px solid #e2e8f0;
  margin-left: 2px;
}

.step-node.current {
  border-left-color: #5a67d8;
}

.step-node.pulse .step-icon {
  animation: pulse 1.5s ease-in-out infinite;
}

.step-row {
  display: flex;
  align-items: baseline;
  gap: 4px;
  padding: 2px 0 2px 4px;
  line-height: 1.5;
  cursor: default;
  border-radius: 3px;
}

.step-row.expandable {
  cursor: pointer;
}

.step-row.expandable:hover {
  background: #f7fafc;
}

.node-arrow {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  font-size: 13px;
  cursor: pointer;
  color: #5a67d8;
  user-select: none;
  text-align: center;
  line-height: 16px;
  border-radius: 3px;
}

.node-arrow:hover {
  background: #edf2f7;
  color: #4c51bf;
}

.node-arrow.leaf {
  cursor: default;
  color: transparent;
}

.node-arrow.leaf:hover {
  background: none;
}

.step-icon {
  flex-shrink: 0;
  font-size: 11px;
}

.step-icon.routing {
  color: #d69e2e;
}
.step-icon.retrieval {
  color: #3182ce;
}
.step-icon.augmentation {
  color: #805ad5;
}
.step-icon.rerank {
  color: #38a169;
}
.step-icon.response_generation {
  color: #e53e3e;
}

.step-title {
  font-weight: 600;
  font-size: 11px;
  color: #2d3748;
  white-space: nowrap;
}

.step-summary {
  font-size: 11px;
  color: #718096;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.step-time {
  font-size: 10px;
  color: #a0aec0;
  flex-shrink: 0;
  white-space: nowrap;
  margin-left: auto;
}

.step-children {
  margin-left: 16px;
}

.step-detail {
  padding: 4px 8px;
  margin-bottom: 2px;
  background: #f7fafc;
  border-radius: 3px;
  border: 1px solid #e2e8f0;
}

.detail-row {
  margin-bottom: 2px;
  font-size: 11px;
}

.detail-row:last-child {
  margin-bottom: 0;
}

.detail-key {
  font-weight: 600;
  color: #4a5568;
  margin-right: 4px;
}

.detail-value {
  color: #2d3748;
}

.detail-value-code {
  display: block;
  font-size: 10px;
  color: #2d3748;
  background: #edf2f7;
  padding: 4px 6px;
  border-radius: 3px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 2px 0 0 0;
  max-height: 150px;
}

.thinking-dots::after {
  content: '';
  animation: dots 1.5s steps(4, end) infinite;
}

@keyframes dots {
  0%,
  20% {
    content: '';
  }
  40% {
    content: '.';
  }
  60% {
    content: '..';
  }
  80%,
  100% {
    content: '...';
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}
</style>
